import { useEffect, useState } from 'react';
import { Settings, Wrench, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

const MaintenanceMode = () => {
  const { t } = useTranslation('common');
  const [glitchActive, setGlitchActive] = useState(false);
  const [dots, setDots] = useState('');

  // Periodic glitch effect
  useEffect(() => {
    const interval = setInterval(() => {
      setGlitchActive(true);
      setTimeout(() => setGlitchActive(false), 200);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Animated dots
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-background relative overflow-hidden flex items-center justify-center p-4">
      {/* Animated background grid */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `linear-gradient(hsl(var(--primary)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)`,
        backgroundSize: '60px 60px',
      }} />

      {/* Floating gradient orbs */}
      <div className="absolute top-1/4 -left-32 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }} />

      <div className="relative z-10 text-center max-w-lg w-full">
        {/* Meme emoji */}
        <div className="text-8xl mb-6" aria-hidden="true">🔧😴</div>

        {/* Glitchy title */}
        <div className="relative mb-4">
          <h1
            className={`text-5xl md:text-7xl font-black tracking-tighter bg-gradient-to-b from-foreground via-foreground/80 to-foreground/20 bg-clip-text text-transparent select-none transition-transform ${glitchActive ? 'translate-x-1' : ''}`}
          >
            {t('maintenance.title')}
          </h1>
          {glitchActive && (
            <h1
              className="absolute inset-0 text-5xl md:text-7xl font-black tracking-tighter text-primary/30 select-none -translate-x-2 translate-y-1"
              aria-hidden="true"
            >
              {t('maintenance.title')}
            </h1>
          )}
        </div>

        <Card className="border-border/50 bg-card/80 backdrop-blur-sm">
          <CardHeader className="pb-3">
            <div className="mx-auto mb-3 h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Wrench className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="text-xl">{t('maintenance.heading')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              {t('maintenance.description')}
            </p>

            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50 border border-border/50">
              <Settings className="h-4 w-4 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground font-mono">
                {t('maintenance.updating')}{dots}
              </span>
            </div>

            <div className="pt-2">
              <Button
                variant="outline"
                size="lg"
                className="gap-2"
                onClick={() => window.location.reload()}
              >
                <RefreshCw className="h-4 w-4" />
                {t('maintenance.refresh')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Fun footer quote */}
        <p className="mt-10 text-sm text-muted-foreground/60 italic">
          "{t('maintenance.funQuote')}"
        </p>
      </div>
    </div>
  );
};

export default MaintenanceMode;
